import { MODEL_LABEL } from '../../lib/format';
import type {
  AccessCode,
  AccessEvent,
  AccessRule,
  AuditEntry,
  Building,
  Credential,
  Door,
  Group,
  GroupManager,
  GroupMember,
  InstallerAssignment,
  Location,
  Lock,
  LockGrant,
  LockHardening,
  Membership,
  Organization,
  Person,
  PlatformStaff,
  Profile,
  SyncJob,
  CardLoan,
} from '../../lib/types';

/** Entirely fictional demo data. No real clubs, people or devices. */

/** Demo-variant van public.person_invites (alleen wat het portaal toont). */
export interface DemoInvite {
  id: string;
  organization_id: string;
  person_id: string;
  email: string;
  created_by: string | null;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  guardian_informed_at: string | null;
}

export interface DemoDb {
  profiles: Profile[];
  platform_staff: PlatformStaff[];
  organizations: Organization[];
  memberships: Membership[];
  installer_assignments: InstallerAssignment[];
  card_loans: CardLoan[];
  locations: Location[];
  buildings: Building[];
  locks: Lock[];
  doors: Door[];
  persons: Person[];
  groups: Group[];
  group_managers: GroupManager[];
  group_members: GroupMember[];
  access_rules: AccessRule[];
  lock_grants: LockGrant[];
  credentials: Credential[];
  lock_hardening: LockHardening[];
  access_codes: AccessCode[];
  access_events: AccessEvent[];
  sync_jobs: SyncJob[];
  audit_log: AuditEntry[];
  person_invites: DemoInvite[];
  /** auth.users.last_sign_in_at per profiel. */
  last_sign_in: Record<string, string>;
  /** auth.users.email per profiel. */
  emails: Record<string, string>;
  /** Profielen met een geverifieerde authenticator-app (auth.mfa_factors). */
  totp_profiles: string[];
}

export const DEMO_ORG_ID = 'org-vv-demo';
export const DEMO_ORG2_ID = 'org-sv-voorbeeld';
/** Nieuwe club midden in de onboarding (sloten toegewezen, nog niet alles gekoppeld). */
export const DEMO_ORG_NEW_ID = 'org-vv-nieuwbouw';

export const DEMO_PERSONAS = [
  {
    profileId: 'prof-platform',
    name: 'Iris Platform',
    role: 'platform_admin' as const,
    description: 'Antreja-team: clubs, voorraad, installateurs en meekijken bij clubs.',
  },
  {
    profileId: 'prof-clubadmin',
    name: 'Sanne de Vries',
    role: 'club_admin' as const,
    description: 'Accommodatiebeheerder bij VV Demo, groepsbeheerder bij SV Voorbeeld.',
  },
  {
    profileId: 'prof-gm',
    name: 'Kees Jansen',
    role: 'group_manager' as const,
    description: 'Teammanager JO17-1 bij VV Demo: eigen groep en codes tot 7 dagen.',
  },
  {
    profileId: 'prof-inst1',
    name: 'Ruud Monteur',
    role: 'installer' as const,
    description: 'Installateur van Antreja: maakt deuren aan en koppelt sloten bij VV Demo en VV Nieuwbouw.',
  },
];

/** Deterministic PRNG so the demo looks the same on every load. */
function mulberry32(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function createDemoDb(now = new Date()): DemoDb {
  const rand = mulberry32(2026);
  const h = (hours: number) => new Date(now.getTime() - hours * 3600_000).toISOString();
  const inDays = (days: number, hour = 23, minute = 59) => {
    const d = new Date(now);
    d.setDate(d.getDate() + days);
    d.setHours(hour, minute, 0, 0);
    return d.toISOString();
  };
  const created = h(24 * 60);

  const profiles: Profile[] = [
    { id: 'prof-platform', full_name: 'Iris Platform', phone: null, locale: 'nl' },
    { id: 'prof-support', full_name: 'Tom Support', phone: null, locale: 'nl' },
    { id: 'prof-clubadmin', full_name: 'Sanne de Vries', phone: null, locale: 'nl' },
    { id: 'prof-clubadmin2', full_name: 'Henk Bakker', phone: null, locale: 'nl' },
    { id: 'prof-gm', full_name: 'Kees Jansen', phone: null, locale: 'nl' },
    { id: 'prof-gm2', full_name: 'Fatima el Amrani', phone: null, locale: 'nl' },
    { id: 'prof-inst1', full_name: 'Ruud Monteur', phone: null, locale: 'nl' },
    { id: 'prof-inst2', full_name: 'Lisa Installatie', phone: null, locale: 'nl' },
    { id: 'prof-nb-admin', full_name: 'Marieke Smit', phone: null, locale: 'nl' },
  ];

  const organizations: Organization[] = [
    {
      id: DEMO_ORG_ID,
      name: 'VV Demo',
      kvk_number: '00000001',
      status: 'active',
      timezone: 'Europe/Amsterdam',
      log_retention_days: 90,
      revoke_alert_hours: 24,
      battery_alert_pct: 25,
      created_at: created,
      archived_at: null,
    },
    {
      id: DEMO_ORG2_ID,
      name: 'SV Voorbeeld',
      kvk_number: '00000002',
      status: 'trial',
      timezone: 'Europe/Amsterdam',
      log_retention_days: 90,
      revoke_alert_hours: 48,
      battery_alert_pct: 25,
      created_at: h(24 * 10),
      archived_at: null,
    },
    {
      id: DEMO_ORG_NEW_ID,
      name: 'VV Nieuwbouw',
      kvk_number: null,
      status: 'trial',
      timezone: 'Europe/Amsterdam',
      log_retention_days: 90,
      revoke_alert_hours: 24,
      battery_alert_pct: 25,
      created_at: h(24 * 2),
      archived_at: null,
    },
  ];

  const memberships: Membership[] = [
    { organization_id: DEMO_ORG_ID, profile_id: 'prof-clubadmin', role: 'club_admin', created_at: created },
    { organization_id: DEMO_ORG_ID, profile_id: 'prof-clubadmin2', role: 'club_admin', created_at: created },
    { organization_id: DEMO_ORG_ID, profile_id: 'prof-gm', role: 'group_manager', created_at: created },
    { organization_id: DEMO_ORG_ID, profile_id: 'prof-gm2', role: 'group_manager', created_at: created },
    { organization_id: DEMO_ORG2_ID, profile_id: 'prof-clubadmin', role: 'group_manager', created_at: h(24 * 9) },
    { organization_id: DEMO_ORG_NEW_ID, profile_id: 'prof-nb-admin', role: 'club_admin', created_at: h(24 * 2) },
  ];

  const loc = (
    id: string,
    org: string,
    name: string,
    [street, house_number, house_number_addition, postal_code, city]: [string, string, string | null, string, string],
  ): Location => ({
    id,
    organization_id: org,
    name,
    street,
    house_number,
    house_number_addition,
    postal_code,
    city,
    country: 'NL',
    address: `${street} ${house_number}${house_number_addition ?? ''}, ${postal_code} ${city}`,
    provider: 'tuya',
    gateway_device_id: null,
  });
  const locations: Location[] = [
    loc('loc-1', DEMO_ORG_ID, 'Sportpark De Demo', ['Demolaan', '1', null, '1234 AB', 'Voorbeeldstad']),
    loc('loc-2', DEMO_ORG_ID, 'Trainingsveld Oost', ['Oostelijke Veldweg', '12', 'a', '1234 AC', 'Voorbeeldstad']),
    loc('loc-3', DEMO_ORG2_ID, 'Sportcomplex Voorbeeld', ['Sportlaan', '40', null, '2345 BC', 'Proefdorp']),
    loc('loc-nb-1', DEMO_ORG_NEW_ID, 'Sportpark Nieuwbouw', ['Bouwlaan', '3', null, '3456 CD', 'Voorbeeldstad']),
  ];

  const lock = (id: string, model: Lock['model'], serial: string, org: string | null, status: Lock['status'], battery: number | null, lastSyncH: number | null): Lock => ({
    id,
    provider: 'tuya',
    model,
    serial,
    provider_device_id: status === 'installed' ? `demo-dev-${id}` : null,
    firmware: status === 'in_stock' ? null : '1.4.2',
    api_generation: model === 'D1' ? 'pro' : 'legacy',
    status,
    organization_id: org,
    battery_pct: battery,
    // Zonder gateway is de meting zo oud als het laatste app-bezoek.
    battery_reported_at: battery === null || lastSyncH === null ? null : h(lastSyncH),
    // Demo: lage sloten zijn al per e-mail gemeld.
    battery_alerted_at: battery !== null && battery <= 25 && lastSyncH !== null ? h(Math.max(0, lastSyncH - 1)) : null,
    last_seen_at: lastSyncH === null ? null : h(lastSyncH),
    last_sync_at: lastSyncH === null ? null : h(lastSyncH),
    reset_card_held_by: model === 'D1' ? 'Antreja kantoor' : null,
    created_at: created,
  });

  const locks: Lock[] = [
    lock('lock-1', 'D1', 'DEMO-A1V-0001', DEMO_ORG_ID, 'installed', 82, 2),
    lock('lock-2', 'D1_R', 'DEMO-A1T-0001', DEMO_ORG_ID, 'installed', 14, 31),
    lock('lock-3', 'D1_K', 'DEMO-A1K-0001', DEMO_ORG_ID, 'installed', 67, 5),
    lock('lock-4', 'D1_K', 'DEMO-A1K-0002', DEMO_ORG_ID, 'installed', 27, 20),
    lock('lock-5', 'D1_K', 'DEMO-A1K-0003', DEMO_ORG_ID, 'installed', 9, 70),
    lock('lock-6', 'D1', 'DEMO-A1V-0002', DEMO_ORG_ID, 'installed', 91, 1),
    lock('lock-7', 'D1_R', 'DEMO-A1T-0002', DEMO_ORG_ID, 'installed', 44, 96),
    lock('lock-8', 'D1_R', 'DEMO-A1T-0003', DEMO_ORG_ID, 'assigned', null, null),
    lock('lock-9', 'D1_K', 'DEMO-A1K-0004', DEMO_ORG2_ID, 'installed', 76, 6),
    lock('lock-10', 'D1', 'DEMO-A1V-0003', null, 'in_stock', null, null),
    lock('lock-11', 'D1', 'DEMO-A1V-0004', null, 'in_stock', null, null),
    lock('lock-12', 'D1_K', 'DEMO-A1K-0005', null, 'in_stock', null, null),
    lock('lock-13', 'D1_R', 'DEMO-A1T-0004', null, 'faulty', null, null),
    lock('lock-nb-1', 'D1', 'DEMO-A1V-0005', DEMO_ORG_NEW_ID, 'assigned', null, null),
    lock('lock-nb-2', 'D1_K', 'DEMO-A1K-0006', DEMO_ORG_NEW_ID, 'assigned', null, null),
    lock('lock-nb-3', 'D1_R', 'DEMO-A1T-0005', DEMO_ORG_NEW_ID, 'assigned', null, null),
    lock('lock-14', 'D1_K', 'DEMO-A1K-0007', null, 'in_stock', null, null),
    // Levering op voorraad (laat zoeken, sorteren en pagineren op de voorraadpagina zien).
    ...Array.from({ length: 120 }, (_, i) => {
      const m = (['D1', 'D1_K', 'D1_R'] as const)[i % 3]!;
      return lock(`lock-stock-${i + 1}`, m, `DEMO-${MODEL_LABEL[m]}-${String(1001 + i)}`, null, 'in_stock', null, null);
    }),
  ];
  const modelOf = (lockId: string | null) => locks.find((l) => l.id === lockId)?.model ?? null;

  // Gebouwen: Club → Locatie → Gebouw → Deur/ruimte → Slot.
  const bld = (id: string, org: string, location: string, name: string, notes: string | null = null): Building => ({
    id, organization_id: org, location_id: location, name, notes, created_at: created, archived_at: null,
  });
  const buildings: Building[] = [
    bld('bld-kantine', DEMO_ORG_ID, 'loc-1', 'Kantine'),
    bld('bld-kleed', DEMO_ORG_ID, 'loc-1', 'Kleedkamers'),
    bld('bld-ballenhok1', DEMO_ORG_ID, 'loc-1', 'Ballenhok 1', 'Naast veld 2'),
    bld('bld-oost', DEMO_ORG_ID, 'loc-2', 'Ballenhok 2'),
    bld('bld-o2-kantine', DEMO_ORG2_ID, 'loc-3', 'Kantine'),
    bld('bld-nb-clubhuis', DEMO_ORG_NEW_ID, 'loc-nb-1', 'Clubhuis'),
  ];
  const buildingOf: Record<string, string> = {
    'door-1': 'bld-kantine',
    'door-6': 'bld-kantine',
    'door-2': 'bld-ballenhok1',
    'door-3': 'bld-kleed',
    'door-4': 'bld-kleed',
    'door-5': 'bld-kleed',
    'door-7': 'bld-oost',
    'door-8': 'bld-oost',
    'door-9': 'bld-o2-kantine',
    'door-nb-1': 'bld-nb-clubhuis',
    'door-nb-2': 'bld-nb-clubhuis',
  };

  const door = (id: string, loc: string, name: string, kind: Door['kind'], lockId: string | null, org = DEMO_ORG_ID, passage = false): Door => ({
    id,
    organization_id: org,
    location_id: loc,
    building_id: buildingOf[id] ?? null,
    name,
    kind,
    lock_id: lockId,
    expected_model: modelOf(lockId),
    door_thickness_mm: 54,
    escape_route: false,
    passage_mode_allowed: passage,
    created_at: created,
    archived_at: null,
  });

  const doors: Door[] = [
    { ...door('door-1', 'loc-1', 'Hoofdingang clubhuis', 'exterior', 'lock-1', DEMO_ORG_ID, true), escape_route: true },
    door('door-2', 'loc-1', 'Materiaalhok', 'exterior', 'lock-2'),
    door('door-3', 'loc-1', 'Kleedkamer 1', 'interior', 'lock-3'),
    door('door-4', 'loc-1', 'Kleedkamer 3', 'interior', 'lock-4'),
    door('door-5', 'loc-1', 'Scheidsrechterskamer', 'interior', 'lock-5'),
    door('door-6', 'loc-1', 'Bestuurskamer', 'interior', 'lock-6'),
    door('door-7', 'loc-2', 'Ballenhok Oost', 'exterior', 'lock-7'),
    door('door-8', 'loc-2', 'Lichtmast-kast', 'exterior', null),
    door('door-9', 'loc-3', 'Kantine', 'interior', 'lock-9', DEMO_ORG2_ID),
    door('door-nb-1', 'loc-nb-1', 'Hoofdingang kantine', 'exterior', 'lock-nb-1', DEMO_ORG_NEW_ID),
    { ...door('door-nb-2', 'loc-nb-1', 'Kleedkamer thuis', 'interior', null, DEMO_ORG_NEW_ID), expected_model: 'D1_K' },
  ];

  const names = [
    'Anouk Visser', 'Bram de Boer', 'Chantal Mulder', 'Daan Smit', 'Esra Yilmaz', 'Frank Meijer',
    'Gijs van Dijk', 'Hanna Bos', 'Ivo Vos', 'Jasmijn Peters', 'Kevin Hendriks', 'Lotte van Leeuwen',
    'Mohamed Bouzid', 'Nina Dekker', 'Olaf Brouwer', 'Priya Ramdin', 'Quinten de Wit', 'Romy Dijkstra',
    'Stijn Smits', 'Tess de Graaf', 'Umut Kaya', 'Vera Schouten', 'Willem van der Meer', 'Yara Kok',
    'Kees Jansen', 'Sanne de Vries',
  ];
  const persons: Person[] = names.map((n, i) => ({
    id: `pers-${i + 1}`,
    organization_id: DEMO_ORG_ID,
    profile_id: n === 'Kees Jansen' ? 'prof-gm' : n === 'Sanne de Vries' ? 'prof-clubadmin' : i % 3 === 0 ? null : `prof-member-${i}`,
    full_name: n,
    email: `${n.toLowerCase().replace(/[^a-z]+/g, '.')}@voorbeeld.invalid`,
    phone: null,
    external_ref: `DEMO${String(1000 + i)}`,
    is_minor: i === 16 || i === 23,
    biometric_consent_at: i % 4 === 0 ? created : null,
    created_at: created,
    archived_at: null,
  }));
  persons.push({
    id: 'pers-o2-1',
    organization_id: DEMO_ORG2_ID,
    profile_id: null,
    full_name: 'Joost Voorbeeld',
    email: null,
    phone: null,
    external_ref: 'VB001',
    is_minor: false,
    biometric_consent_at: null,
    created_at: h(200),
    archived_at: null,
  });
  // Clubbeheerder van de nieuwe club staat ook als persoon in de club (add_club_admin).
  persons.push({
    id: 'pers-nb-1',
    organization_id: DEMO_ORG_NEW_ID,
    profile_id: 'prof-nb-admin',
    full_name: 'Marieke Smit',
    email: 'marieke.smit@voorbeeld.invalid',
    phone: null,
    external_ref: null,
    is_minor: false,
    biometric_consent_at: null,
    created_at: h(48),
    archived_at: null,
  });

  const season = new Date(now.getFullYear() + (now.getMonth() >= 6 ? 1 : 0), 5, 30, 23, 59).toISOString();
  const groups: Group[] = [
    { id: 'grp-bar', organization_id: DEMO_ORG_ID, name: 'Barvrijwilligers 2026/27', season_label: '2026/27', valid_from: created, valid_until: season, created_at: created, archived_at: null },
    { id: 'grp-jo17', organization_id: DEMO_ORG_ID, name: 'JO17-1 staf', season_label: '2026/27', valid_from: created, valid_until: null, created_at: created, archived_at: null },
    { id: 'grp-trainers', organization_id: DEMO_ORG_ID, name: 'Trainers senioren', season_label: '2026/27', valid_from: created, valid_until: null, created_at: created, archived_at: null },
    { id: 'grp-bestuur', organization_id: DEMO_ORG_ID, name: 'Bestuur', season_label: null, valid_from: created, valid_until: null, created_at: created, archived_at: null },
    { id: 'grp-terrein', organization_id: DEMO_ORG_ID, name: 'Terreinploeg', season_label: null, valid_from: created, valid_until: null, created_at: created, archived_at: null },
    { id: 'grp-o2', organization_id: DEMO_ORG2_ID, name: 'Kantinedienst', season_label: '2026/27', valid_from: created, valid_until: null, created_at: created, archived_at: null },
  ];

  const group_managers: GroupManager[] = [
    { group_id: 'grp-jo17', profile_id: 'prof-gm' },
    { group_id: 'grp-bar', profile_id: 'prof-gm2' },
    { group_id: 'grp-o2', profile_id: 'prof-clubadmin' },
  ];

  const gm = (group_id: string, idx: number, validUntil: string | null = null): GroupMember => ({
    group_id,
    person_id: `pers-${idx}`,
    valid_from: created,
    valid_until: validUntil,
    added_by: 'prof-clubadmin',
  });
  const group_members: GroupMember[] = [
    ...[1, 2, 3, 4, 5, 6, 7, 8].map((i) => gm('grp-bar', i, i === 3 ? inDays(9) : i === 7 ? inDays(20) : null)),
    ...[9, 10, 11, 25].map((i) => gm('grp-jo17', i, i === 11 ? inDays(4) : null)),
    ...[12, 13, 14, 15].map((i) => gm('grp-trainers', i)),
    ...[16, 26, 18].map((i) => gm('grp-bestuur', i)),
    ...[19, 20, 21].map((i) => gm('grp-terrein', i, i === 21 ? inDays(14) : null)),
    { group_id: 'grp-o2', person_id: 'pers-o2-1', valid_from: created, valid_until: null, added_by: 'prof-clubadmin' },
    // Uitleenpassen voor gasten (beheerd door de groepsbeheerder van JO17-1 staf).
    { group_id: 'grp-jo17', person_id: 'pers-loan-1', valid_from: created, valid_until: null, added_by: 'prof-clubadmin' },
    { group_id: 'grp-jo17', person_id: 'pers-loan-2', valid_from: created, valid_until: null, added_by: 'prof-clubadmin' },
  ];
  for (const n of [1, 2]) {
    persons.push({ id: `pers-loan-${n}`, organization_id: DEMO_ORG_ID, profile_id: null, full_name: `Uitleenpas ${n}`, email: null, phone: null, external_ref: null, is_minor: false, biometric_consent_at: null, created_at: created, archived_at: null, is_loan_card: true });
  }

  const rule = (id: string, group_id: string, door_id: string, weekdays: number, start: number, end: number, all_day = false, org = DEMO_ORG_ID): AccessRule => ({
    id,
    organization_id: org,
    group_id,
    door_id,
    weekdays,
    start_minute: start,
    end_minute: end,
    all_day,
  });
  const access_rules: AccessRule[] = [
    rule('rule-1', 'grp-bar', 'door-1', 127, 8 * 60, 24 * 60),
    rule('rule-2', 'grp-jo17', 'door-2', 2 | 8 | 32, 17 * 60, 22 * 60),
    rule('rule-3', 'grp-jo17', 'door-3', 2 | 8 | 32, 17 * 60, 22 * 60),
    rule('rule-4', 'grp-trainers', 'door-2', 2 | 8, 18 * 60, 22 * 60),
    rule('rule-5', 'grp-trainers', 'door-4', 2 | 8, 18 * 60, 22 * 60),
    rule('rule-6', 'grp-bestuur', 'door-1', 127, 0, 1440, true),
    rule('rule-7', 'grp-bestuur', 'door-6', 127, 0, 1440, true),
    rule('rule-8', 'grp-terrein', 'door-2', 31, 7 * 60, 17 * 60),
    rule('rule-9', 'grp-terrein', 'door-7', 31, 7 * 60, 17 * 60),
    rule('rule-10', 'grp-o2', 'door-9', 96, 8 * 60, 23 * 60, false, DEMO_ORG2_ID),
  ];

  const credentials: Credential[] = [];
  persons.forEach((p, i) => {
    if (p.organization_id !== DEMO_ORG_ID) return;
    if (p.profile_id) {
      credentials.push({ id: `cred-app-${i}`, organization_id: DEMO_ORG_ID, person_id: p.id, type: 'app', lock_id: null, label: 'App', status: 'active', valid_until: null, created_at: created });
    }
    if (i % 2 === 0) {
      credentials.push({
        id: `cred-card-${i}`,
        organization_id: DEMO_ORG_ID,
        person_id: p.id,
        type: 'card',
        lock_id: 'lock-1',
        label: `Pas #${10 + i}`,
        status: i === 4 ? 'revoke_pending' : i === 10 ? 'pending_enrollment' : 'active',
        valid_until: null,
        created_at: created,
      });
    }
    if (p.biometric_consent_at && i % 8 === 0) {
      credentials.push({ id: `cred-fp-${i}`, organization_id: DEMO_ORG_ID, person_id: p.id, type: 'fingerprint', lock_id: 'lock-6', label: 'Rechterduim', status: 'active', valid_until: null, created_at: created });
    }
  });

  // Ingeleerd door de installateur met de app (register_enrolled_credential).
  credentials.push(
    { id: 'cred-loan-1', organization_id: DEMO_ORG_ID, person_id: 'pers-loan-1', type: 'card', lock_id: 'lock-1', label: 'Uitleenpas 1', status: 'active', valid_until: null, enrolled_by: 'prof-inst1', created_at: created },
    { id: 'cred-loan-2', organization_id: DEMO_ORG_ID, person_id: 'pers-loan-2', type: 'card', lock_id: 'lock-1', label: 'Uitleenpas 2', status: 'active', valid_until: null, enrolled_by: 'prof-inst1', created_at: created },
  );
  credentials.push(
    { id: 'cred-inst-1', organization_id: DEMO_ORG_ID, person_id: 'pers-26', person_label: 'Sanne de Vries', type: 'pin', lock_id: 'lock-3', label: 'Pincode 1', status: 'active', valid_until: null, enrolled_by: 'prof-inst1', created_at: h(26) },
    { id: 'cred-inst-2', organization_id: DEMO_ORG_ID, person_id: 'pers-25', person_label: 'Kees Jansen', type: 'card', lock_id: 'lock-3', label: 'Pas 1', status: 'active', valid_until: null, enrolled_by: 'prof-inst1', created_at: h(25) },
    { id: 'cred-inst-3', organization_id: DEMO_ORG_ID, person_id: null, person_label: 'Groundsman Piet', type: 'card', lock_id: 'lock-7', label: 'Pas 1', status: 'active', valid_until: null, enrolled_by: 'prof-inst1', created_at: h(97) },
  );

  // Hardening-checklist per slot (save_lock_hardening vanuit de installateursapp).
  const yes = (...itemIds: string[]) => itemIds.map((itemId) => ({ itemId, answer: 'yes' as const }));
  const common = ['auto_lock_on', 'passage_mode_off', 'clock_synced'];
  const hardened = (lockId: string, doorId: string, items: LockHardening['items'], completedH: number | null, org = DEMO_ORG_ID): LockHardening => ({
    lock_id: lockId,
    organization_id: org,
    door_id: doorId,
    items,
    completed: completedH !== null,
    completed_at: completedH === null ? null : h(completedH),
    recorded_by: org === DEMO_ORG_ID ? 'prof-inst1' : 'prof-inst2',
    updated_at: h(completedH ?? 30),
  });
  const keys = { itemId: 'emergency_keys', answer: 'yes' as const, text: '2, kluis bestuurskamer' };
  const lock_hardening: LockHardening[] = [
    hardened('lock-1', 'door-1', [...yes('random_finger_refused', ...common), { itemId: 'reset_card_envelope', answer: 'yes', text: 'ENV-0041' }, keys], 24 * 20),
    hardened('lock-2', 'door-2', [...yes(...common), keys], 24 * 20),
    hardened('lock-3', 'door-3', [...yes('default_code_refused', ...common), keys], 24 * 19),
    hardened('lock-4', 'door-4', [...yes('default_code_refused', ...common), keys], 24 * 19),
    // Nog niet afgerond: noodsleutels nog niet vastgelegd.
    hardened('lock-5', 'door-5', [...yes('default_code_refused', 'auto_lock_on', 'clock_synced'), { itemId: 'emergency_keys', answer: 'no' }], null),
    hardened('lock-6', 'door-6', [...yes('random_finger_refused', ...common), { itemId: 'reset_card_envelope', answer: 'yes', text: 'ENV-0042' }, keys], 24 * 18),
    hardened('lock-9', 'door-9', [...yes('default_code_refused', ...common), keys], 24 * 8, DEMO_ORG2_ID),
  ];

  const job = (id: string, lockId: string, action: SyncJob['action'], ageH: number, status: SyncJob['status'] = 'awaiting_ble', org = DEMO_ORG_ID): SyncJob => ({
    id,
    organization_id: org,
    lock_id: lockId,
    action,
    status,
    attempts: status === 'failed' ? 3 : 0,
    last_error: status === 'failed' ? 'Slot reageerde niet binnen 30 s' : null,
    requires_ble: true,
    created_at: h(ageH),
    completed_at: null,
  });
  const sync_jobs: SyncJob[] = [
    job('job-1', 'lock-1', 'remove_credential', 30),
    job('job-2', 'lock-2', 'set_schedule', 52),
    job('job-3', 'lock-2', 'upsert_member', 50),
    job('job-4', 'lock-2', 'upsert_member', 6),
    job('job-5', 'lock-4', 'upsert_member', 3),
    job('job-6', 'lock-7', 'remove_member', 80),
    job('job-7', 'lock-5', 'pull_logs', 12, 'failed'),
    job('job-8', 'lock-9', 'upsert_member', 4, 'awaiting_ble', DEMO_ORG2_ID),
  ];

  // grants: derived from rules (synced except where a job is open)
  const lock_grants: LockGrant[] = [];
  for (const m of group_members) {
    const p = persons.find((x) => x.id === m.person_id)!;
    for (const r of access_rules.filter((x) => x.group_id === m.group_id)) {
      const d = doors.find((x) => x.id === r.door_id);
      if (!d?.lock_id) continue;
      if (lock_grants.some((g) => g.person_id === p.id && g.lock_id === d.lock_id)) continue;
      const pending = sync_jobs.some((j) => j.lock_id === d.lock_id && j.action === 'upsert_member') && rand() < 0.4;
      lock_grants.push({
        id: `grant-${lock_grants.length + 1}`,
        organization_id: p.organization_id,
        person_id: p.id,
        lock_id: d.lock_id,
        valid_from: m.valid_from,
        valid_until: m.valid_until,
        schedule: [{ weekdays: r.weekdays, start_minute: r.all_day ? 0 : r.start_minute, end_minute: r.all_day ? 1440 : r.end_minute }],
        sync_state: pending ? 'pending' : 'synced',
        last_synced_at: pending ? null : h(30),
      });
    }
  }

  const today = (hh: number, mm = 0) => {
    const d = new Date(now);
    d.setHours(hh, mm, 0, 0);
    return d.toISOString();
  };
  const nextSaturday = new Date(now);
  nextSaturday.setDate(now.getDate() + ((6 - now.getDay() + 7) % 7 || 7));
  const sat = (hh: number) => {
    const d = new Date(nextSaturday);
    d.setHours(hh, 0, 0, 0);
    return d.toISOString();
  };
  const access_codes: AccessCode[] = [
    { id: 'code-1', organization_id: DEMO_ORG_ID, lock_id: 'lock-5', kind: 'offline_window', label: 'Scheidsrechter JO15-2', recipient_name: 'M. Fluit', recipient_contact: 'scheids@voorbeeld.invalid', valid_from: today(8), valid_until: today(18), revoked_at: null, created_by: 'prof-clubadmin', created_at: h(26) },
    { id: 'code-2', organization_id: DEMO_ORG_ID, lock_id: 'lock-4', kind: 'offline_window', label: 'Uitteam kleedkamer 3', recipient_name: 'Gastclub', recipient_contact: null, valid_from: sat(8), valid_until: sat(18), revoked_at: null, created_by: 'prof-clubadmin', created_at: h(5) },
    { id: 'code-3', organization_id: DEMO_ORG_ID, lock_id: 'lock-3', kind: 'offline_once', label: 'Fysiotherapeut', recipient_name: 'P. Hersteller', recipient_contact: '0600000000', valid_from: h(72), valid_until: h(66), revoked_at: null, created_by: 'prof-gm', created_at: h(73) },
    { id: 'code-4', organization_id: DEMO_ORG_ID, lock_id: 'lock-3', kind: 'offline_window', label: 'Toernooi JO11', recipient_name: null, recipient_contact: null, valid_from: h(30), valid_until: inDays(2, 20, 0), revoked_at: h(20), created_by: 'prof-clubadmin', created_at: h(40) },
  ];

  const access_events: AccessEvent[] = [];
  const methods: AccessEvent['method'][] = ['app', 'app', 'card', 'card', 'card', 'fingerprint', 'temp_code'];
  const doorWithLocks = doors.filter((d) => d.lock_id && d.organization_id === DEMO_ORG_ID);
  for (let i = 0; i < 140; i++) {
    const d = doorWithLocks[Math.floor(rand() * doorWithLocks.length)]!;
    const method = methods[Math.floor(rand() * methods.length)]!;
    const result: AccessEvent['result'] = rand() < 0.08 ? 'denied' : 'granted';
    const person = method === 'temp_code' ? null : persons[Math.floor(rand() * 24)]!;
    const occurred = h(rand() * 24 * 8);
    access_events.push({
      id: i + 1,
      organization_id: DEMO_ORG_ID,
      lock_id: d.lock_id!,
      door_id: d.id,
      person_id: person?.id ?? null,
      occurred_at: occurred,
      received_at: occurred,
      method,
      result,
    });
  }
  access_events.sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));

  // Beheerlogboek (nieuwste eerst). before/after zoals de database ze vastlegt.
  const audit = (
    id: number, hours: number, actor: string, role: string, action: string, entity: string, entity_id: string | null,
    after: Record<string, unknown> | null = null, before: Record<string, unknown> | null = null, org: string | null = DEMO_ORG_ID,
  ): AuditEntry => ({ id, organization_id: org, actor_profile_id: actor, actor_role: role, action, entity, entity_id, created_at: h(hours), after, before });
  const audit_log: AuditEntry[] = [
    audit(16, 1.5, 'prof-clubadmin', 'club_admin', 'location.update', 'locations', 'loc-2',
      { name: 'Trainingsveld Oost', address: 'Oostelijke Veldweg 12a, 1234 AC Voorbeeldstad' }, { name: 'Trainingsveld Oost', address: null }),
    audit(15, 3, 'prof-gm', 'group_manager', 'group_member.add', 'group_members', 'grp-jo17:pers-2'),
    audit(14, 6, 'prof-inst1', 'installer', 'lock.battery_replaced', 'locks', 'lock-6', { battery_pct: 100 }, { battery_pct: 31 }),
    audit(13, 26, 'prof-clubadmin', 'club_admin', 'access_code.create', 'access_codes', 'code-1', { label: 'Scheidsrechter JO15-2' }),
    audit(12, 26, 'prof-clubadmin', 'club_admin', 'person_invite.create', 'persons', 'pers-1'),
    audit(11, 30, 'prof-clubadmin', 'club_admin', 'credential.revoke', 'credentials', 'cred-card-4'),
    audit(10, 52, 'prof-clubadmin', 'club_admin', 'access_rules.save', 'groups', 'grp-jo17'),
    audit(9, 24 * 4, 'prof-platform', 'platform_admin', 'impersonate.start', 'organizations', DEMO_ORG_ID, { reason: 'Hulp bij het koppelen van de sloten' }),
    audit(8, 24 * 18, 'prof-inst1', 'installer', 'lock_hardening.save', 'locks', 'lock-3', { completed: true }),
    audit(7, 24 * 19, 'prof-inst1', 'installer', 'door.link_lock', 'doors', 'door-3', { lock_id: 'lock-3', serial: 'DEMO-D1K-0001', model: 'D1_K' }),
    audit(6, 24 * 19.5, 'prof-inst1', 'installer', 'door.create', 'doors', 'door-3', { name: 'Kleedkamer 1', building_id: 'bld-kleed', location_id: 'loc-1', kind: 'interior' }),
    audit(5, 24 * 20, 'prof-clubadmin', 'club_admin', 'building.create', 'buildings', 'bld-kleed', { name: 'Kleedkamers', location_id: 'loc-1' }),
    audit(4, 24 * 21, 'prof-clubadmin', 'club_admin', 'club_admin.add', 'memberships', `${DEMO_ORG_ID}:prof-gm`, { role: 'group_manager' }),
    audit(3, 24 * 22, 'prof-clubadmin', 'club_admin', 'organization.update', 'organizations', DEMO_ORG_ID,
      { name: 'VV Demo', kvk_number: '00000001' }, { name: 'VV Demo', kvk_number: null }),
    audit(2, 24 * 59, 'prof-platform', 'platform_admin', 'organization.create', 'organizations', DEMO_ORG_ID),
    audit(1, 240, 'prof-platform', 'platform_admin', 'organization.create', 'organizations', DEMO_ORG2_ID, null, null, null),
  ];

  // App-uitnodigingen: Anouk (pers-1) is 2 dagen geleden uitgenodigd, Daan (pers-4) heeft een verlopen uitnodiging.
  const person_invites: DemoInvite[] = [
    { id: 'inv-1', organization_id: DEMO_ORG_ID, person_id: 'pers-1', email: persons[0]!.email!, created_by: 'prof-clubadmin', created_at: h(48), expires_at: inDays(28), accepted_at: null, revoked_at: null, guardian_informed_at: null },
    { id: 'inv-2', organization_id: DEMO_ORG_ID, person_id: 'pers-4', email: persons[3]!.email!, created_by: 'prof-clubadmin', created_at: h(24 * 40), expires_at: h(24 * 10), accepted_at: null, revoked_at: null, guardian_informed_at: null },
  ];
  const last_sign_in: Record<string, string> = {
    'prof-clubadmin': h(1),
    'prof-gm': h(5),
    'prof-platform': h(0.2),
    'prof-support': h(26),
    'prof-inst1': h(3),
    'prof-clubadmin2': h(24 * 6),
    'prof-gm2': h(30),
  };
  const emails: Record<string, string> = {
    'prof-platform': 'iris@antreja.invalid',
    'prof-support': 'tom@antreja.invalid',
    'prof-inst1': 'ruud@antreja.invalid',
    'prof-inst2': 'lisa@antreja.invalid',
    'prof-clubadmin': 'sanne.de.vries@voorbeeld.invalid',
    'prof-clubadmin2': 'henk.bakker@voorbeeld.invalid',
    'prof-gm': 'kees.jansen@voorbeeld.invalid',
    'prof-gm2': 'fatima.el.amrani@voorbeeld.invalid',
    'prof-nb-admin': 'marieke.smit@voorbeeld.invalid',
  };
  persons.forEach((p, i) => {
    if (p.profile_id?.startsWith('prof-member-')) last_sign_in[p.profile_id] = h(6 + ((i * 37) % 200));
  });

  return {
    person_invites,
    last_sign_in,
    emails,
    totp_profiles: ['prof-platform', 'prof-support', 'prof-inst1', 'prof-clubadmin', 'prof-clubadmin2'],
    profiles,
    platform_staff: [
      { profile_id: 'prof-platform', role: 'platform_admin' },
      { profile_id: 'prof-support', role: 'support' },
      { profile_id: 'prof-inst1', role: 'installer' },
      { profile_id: 'prof-inst2', role: 'installer' },
    ],
    organizations,
    memberships,
    installer_assignments: [
      { organization_id: DEMO_ORG_ID, profile_id: 'prof-inst1', valid_until: inDays(45) },
      { organization_id: DEMO_ORG2_ID, profile_id: 'prof-inst2', valid_until: inDays(10) },
      { organization_id: DEMO_ORG_NEW_ID, profile_id: 'prof-inst1', valid_until: inDays(30) },
    ],
    locations,
    buildings,
    locks,
    doors,
    persons,
    groups,
    group_managers,
    group_members,
    access_rules,
    lock_grants,
    credentials,
    card_loans: [
      { id: 'loan-1', organization_id: DEMO_ORG_ID, person_id: 'pers-loan-1', guest_name: 'Jan de Vries', guest_club: 'VV Gouda JO17-1', loaned_at: h(2), due_at: inDays(0.3), returned_at: null, issued_by: 'prof-gm', returned_by: null, created_at: h(2) },
    ],
    lock_hardening,
    access_codes,
    access_events,
    sync_jobs,
    audit_log,
  };
}
